import { useMemo, useState, useEffect } from "react";
import { addDays, format, isSameDay, startOfWeek } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { AgendaSettings, AppointmentStatus } from "@bookdental/schemas";
import { MobileDayView } from "./MobileDayView";

type Appt = {
  id: string;
  patientName: string;
  startTime: string;
  endTime: string;
  status: AppointmentStatus;
  color?: string | null;
  date: Date | string;
};

type Props = {
  anchorDate: Date;
  appointments: Appt[];
  settings: AgendaSettings;
  onAppointmentClick?: (id: string) => void;
};

const WEEK_DAY_LABELS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"];

export function MobileWeekView({
  anchorDate,
  appointments,
  settings,
  onAppointmentClick,
}: Props) {
  const weekStart = useMemo(
    () => startOfWeek(anchorDate, { weekStartsOn: 0 }),
    [anchorDate],
  );
  const days = useMemo(
    () => Array.from({ length: 7 }).map((_, i) => addDays(weekStart, i)),
    [weekStart],
  );

  const [selected, setSelected] = useState<Date>(anchorDate);
  useEffect(() => {
    setSelected(anchorDate);
  }, [anchorDate]);

  const countsByDay = useMemo(() => {
    const map = new Map<string, number>();
    for (const a of appointments) {
      const k = new Date(a.date).toDateString();
      map.set(k, (map.get(k) ?? 0) + 1);
    }
    return map;
  }, [appointments]);

  const today = new Date();

  return (
    <div className="flex min-h-full">
      <div className="flex w-[68px] shrink-0 flex-col gap-2 bg-zinc-50 p-2">
        {days.map((d) => {
          const isSelected = isSameDay(d, selected);
          const isToday = isSameDay(d, today);
          const count = countsByDay.get(d.toDateString()) ?? 0;
          return (
            <button
              key={d.toISOString()}
              type="button"
              onClick={() => setSelected(d)}
              className={`relative flex flex-col items-center justify-center rounded-xl py-2 text-center transition-all ${
                isSelected
                  ? "bg-brand-600 text-white shadow-md"
                  : isToday
                    ? "bg-white text-brand-700 ring-1 ring-brand-300"
                    : "bg-white text-zinc-700 hover:bg-zinc-100"
              }`}
            >
              <span className="text-[11px] font-bold uppercase leading-tight">
                {WEEK_DAY_LABELS[d.getDay()]}
              </span>
              <span className="text-lg font-bold leading-none tabular-nums">
                {format(d, "d")}
              </span>
              {count > 0 && !isSelected && (
                <span className="mt-1 h-1 w-1 rounded-full bg-brand-600" />
              )}
            </button>
          );
        })}
      </div>

      <div className="min-w-0 flex-1 bg-white">
        <div className="border-b border-zinc-200 px-4 py-2 text-[13px] font-semibold capitalize text-zinc-700">
          {format(selected, "EEEE, dd 'de' MMMM", { locale: ptBR })}
        </div>
        <MobileDayView
          date={selected}
          appointments={appointments}
          settings={settings}
          onAppointmentClick={onAppointmentClick}
        />
      </div>
    </div>
  );
}
